import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Calendar,
  Hammer,
  Star,
  Phone,
  Mail,
  Globe,
  MapPin,
  Loader2,
  Facebook,
  Instagram,
  ExternalLink,
} from 'lucide-react';

const STAGE_ICONS = {
  scheduled: Calendar,
  in_progress: Hammer,
  completed: CheckCircle2,
  reviewed: Star,
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const PublicJobStatusPage = () => {
  const { token } = useParams();
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showThanks, setShowThanks] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch(`${apiBase}/api/public/job-status/${token}`);
      if (!r.ok) throw new Error(r.status === 404 ? 'Project not found.' : `HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
      setError(null);
    } catch (e) {
      setError(e.message || 'Could not load project');
    } finally {
      setLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => { load(); }, [load]);

  const submitReview = async () => {
    if (!rating) {
      toast.error('Please pick a star rating first');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${apiBase}/api/public/job-status/${token}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment: comment.trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not submit review');
      }
      setShowThanks(true);
      toast.success('Thanks for your feedback!');
      await load();
      // If 4-5 stars and a Google review URL exists, nudge to leave a public review
      if (rating >= 4 && data?.org?.google_review_url) {
        setTimeout(() => {
          window.open(data.org.google_review_url, '_blank', 'noopener,noreferrer');
        }, 1200);
      }
    } catch (e) {
      toast.error(e.message || 'Could not submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl shadow-sm border p-8 max-w-md text-center">
          <p className="text-lg font-semibold text-gray-900 mb-1">Project not found</p>
          <p className="text-sm text-gray-500">{error || 'This link may have expired.'}</p>
        </div>
      </div>
    );
  }

  const { job, client, org, stages, current_stage_index: currentIndex, review } = data;
  const primary = org.primary_color || '#1e3a8a';
  const isCompleted = job.status === 'completed';
  const alreadyReviewed = review.submitted;
  const beforePhotos = (job.photos || []).filter(p => /before/i.test(p.tag || ''));
  const afterPhotos = (job.photos || []).filter(p => /after/i.test(p.tag || ''));
  const otherPhotos = (job.photos || []).filter(p => !/before|after/i.test(p.tag || ''));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-job-status-page">
      {/* Top bar */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          {org.logo_url && (
            <img src={org.logo_url} alt={org.name} className="h-10 max-w-[120px] object-contain" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide text-gray-500">Project tracker</p>
            <p className="font-bold text-base truncate" style={{ color: primary }}>{org.name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">

        {/* Hero */}
        <div className="bg-white rounded-2xl shadow-sm border p-5 sm:p-6">
          <p className="text-sm text-gray-500 mb-1">Hi {client.first_name || client.name || 'there'} 👋</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{job.title}</h1>
          {job.address && (
            <p className="text-sm text-gray-500 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> {job.address}
            </p>
          )}
          {isCompleted && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-200 text-sm font-semibold">
              <CheckCircle2 className="w-4 h-4" /> Completed {job.completed_at ? `on ${formatDate(job.completed_at)}` : ''}
            </div>
          )}
        </div>

        {/* Tracker */}
        <div className="bg-white rounded-2xl shadow-sm border p-5 sm:p-6" data-testid="tracker-stages">
          <h2 className="font-bold text-lg mb-4" style={{ color: primary }}>Project status</h2>
          <div className="relative">
            {/* connecting line */}
            <div className="absolute left-5 right-5 top-5 h-0.5 bg-gray-200" aria-hidden />
            <div
              className="absolute left-5 top-5 h-0.5 transition-all duration-700"
              style={{
                width: stages.length > 1 ? `calc(${(currentIndex / (stages.length - 1)) * 100}% - ${currentIndex === stages.length - 1 ? '0px' : '20px'})` : 0,
                background: primary,
              }}
              aria-hidden
            />
            <div className="relative grid" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}>
              {stages.map((s, i) => {
                const Icon = STAGE_ICONS[s.key] || CheckCircle2;
                const reached = i <= currentIndex;
                const isCurrent = i === currentIndex;
                return (
                  <div key={s.key} className="flex flex-col items-center text-center" data-testid={`stage-${s.key}`}>
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${reached ? 'shadow-md' : 'bg-gray-100 border border-gray-200'} ${isCurrent ? 'scale-110' : ''}`}
                      style={reached ? { background: primary, color: '#fff' } : { color: '#94a3b8' }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className={`mt-2 text-[11px] sm:text-xs font-semibold ${reached ? 'text-gray-900' : 'text-gray-400'}`}>
                      {s.label}
                    </p>
                    {isCurrent && !alreadyReviewed && (
                      <span className="mt-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase" style={{ background: `${primary}1f`, color: primary }}>
                        Now
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Photos */}
        {(beforePhotos.length > 0 || afterPhotos.length > 0 || otherPhotos.length > 0) && (
          <div className="bg-white rounded-2xl shadow-sm border p-5 sm:p-6" data-testid="photos-section">
            <h2 className="font-bold text-lg mb-4" style={{ color: primary }}>Photos from your project</h2>
            {beforePhotos.length > 0 && afterPhotos.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">Before</p>
                  <div className="grid grid-cols-2 gap-2">
                    {beforePhotos.slice(0, 4).map((p, i) => (
                      <a key={i} href={p.url} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                        <img src={p.url} alt="Before" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">After</p>
                  <div className="grid grid-cols-2 gap-2">
                    {afterPhotos.slice(0, 4).map((p, i) => (
                      <a key={i} href={p.url} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                        <img src={p.url} alt="After" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {otherPhotos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {otherPhotos.slice(0, 8).map((p, i) => (
                  <a key={i} href={p.url} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img src={p.url} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Review (only when completed) */}
        {isCompleted && (
          <div className="bg-white rounded-2xl shadow-sm border p-5 sm:p-6" data-testid="review-section">
            {alreadyReviewed ? (
              <div className="text-center">
                <div className="text-3xl mb-2">🎉</div>
                <h2 className="font-bold text-lg mb-1" style={{ color: primary }}>
                  Thanks for the {review.rating}-star review!
                </h2>
                {review.comment && (
                  <p className="text-sm text-gray-600 italic mt-3 p-3 bg-gray-50 rounded-lg">"{review.comment}"</p>
                )}
                {org.google_review_url && (
                  <a
                    href={org.google_review_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-sm font-semibold underline"
                    style={{ color: primary }}
                  >
                    Share publicly on Google <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ) : (
              <>
                <h2 className="font-bold text-lg mb-1" style={{ color: primary }}>How did we do?</h2>
                <p className="text-sm text-gray-500 mb-4">Your feedback helps {org.name} grow and improve.</p>
                <div className="flex items-center justify-center gap-1.5 mb-4" data-testid="star-picker">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const filled = (hoverRating || rating) >= n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onMouseEnter={() => setHoverRating(n)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(n)}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                        className="p-1.5 transition-transform active:scale-90"
                        data-testid={`star-${n}`}
                      >
                        <Star
                          className={`w-9 h-9 transition-all ${filled ? 'fill-amber-400 stroke-amber-500' : 'stroke-gray-300'}`}
                        />
                      </button>
                    );
                  })}
                </div>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us what stood out (optional)…"
                  rows={3}
                  className="resize-none mb-3"
                  data-testid="review-comment"
                />
                <Button
                  onClick={submitReview}
                  disabled={submitting || !rating}
                  className="w-full h-11 font-semibold"
                  style={{ background: primary }}
                  data-testid="review-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {rating >= 4 && org.google_review_url ? 'Submit & share on Google' : 'Submit feedback'}
                </Button>
                {rating >= 4 && org.google_review_url && !showThanks && (
                  <p className="text-[11px] text-gray-500 text-center mt-2">
                    We'll open Google Reviews in a new tab so you can share publicly too 💚
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Contact + social */}
        <div className="bg-white rounded-2xl shadow-sm border p-5 sm:p-6" data-testid="contact-section">
          <h2 className="font-bold text-base mb-3" style={{ color: primary }}>Need to reach us?</h2>
          <div className="flex flex-wrap gap-2">
            {org.phone && (
              <a href={`tel:${org.phone}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border border-gray-200 hover:bg-gray-50">
                <Phone className="w-4 h-4" /> {org.phone}
              </a>
            )}
            {org.email && (
              <a href={`mailto:${org.email}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border border-gray-200 hover:bg-gray-50">
                <Mail className="w-4 h-4" /> Email
              </a>
            )}
            {org.website && (
              <a href={org.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border border-gray-200 hover:bg-gray-50">
                <Globe className="w-4 h-4" /> Website
              </a>
            )}
            {org.facebook_url && (
              <a href={org.facebook_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border border-gray-200 hover:bg-gray-50 text-[#1877f2]">
                <Facebook className="w-4 h-4" /> Facebook
              </a>
            )}
            {org.instagram_url && (
              <a href={org.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border border-gray-200 hover:bg-gray-50 text-[#c13584]">
                <Instagram className="w-4 h-4" /> Instagram
              </a>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-gray-400 pt-4">
          Powered by ContractorOS
        </p>
      </div>
    </div>
  );
};

export default PublicJobStatusPage;
